// Location de biens d'occasion entre particuliers — Phase 14 (MVP soft,
// façon Allovoisins). Source UNIQUE de vérité côté front pour : les
// catégories éligibles, les unités de tarification et leurs libellés.
//
// ⚠️ Garder synchronisé avec le backend :
//   backend/services/listing_guards.py
//     → RENTAL_ALLOWED_CATEGORIES / RENTAL_ALLOWED_UNITS / DEFAULT_RENTAL_UNIT
//
// Cadrage fondateurs (juin 2026) : on ne loue QUE du matériel d'occasion
// non contraignant, remis en main propre. Exclus : véhicules, animaux,
// services, mode/chaussures (hygiène), livres/culture (proche du prêt).
// « Autres / Divers » (other) est éligible : catch-all pour le matériel qui
// ne rentre dans aucune catégorie (ex. piscine, tente, mobilier de jardin…).

export const RENTAL_ALLOWED_CATEGORIES = ["electronics", "home", "sports", "kids", "other"];
// Services facturés au temps (cours, prestations à l'heure) — rubrique
// « Services » uniquement. Regroupés avec la location sous « Location & services ».
export const SERVICE_ALLOWED_CATEGORIES = ["services"];

export const DEFAULT_RENTAL_UNIT = "day";
export const DEFAULT_SERVICE_UNIT = "hour";

// ⚠️ P2 (D-2026-07-03-7, forward-only) : les NOUVELLES locations s'expriment
// UNIQUEMENT en €/jour (plafond universel 5 jours) — le sélecteur d'unité de
// la publication n'est plus proposé pour une location (StepPrice /
// NewPublishPage forcent "day" ; le backend refuse toute autre unité).
// Cette liste reste la source des LIBELLÉS (suffixe "/sem." d'une annonce
// legacy, sélecteur des SERVICES) — ne pas la réduire : l'affichage des
// annonces existantes en dépend.
export const RENTAL_UNITS = [
  { id: "day", label: "par jour", short: "jour", suffix: "/jour" },
  { id: "hour", label: "par heure", short: "heure", suffix: "/h" },
  { id: "week", label: "par semaine", short: "semaine", suffix: "/sem." },
  { id: "month", label: "par mois", short: "mois", suffix: "/mois" },
];

// SERVICES — DEUX modes de tarification EXACTEMENT (décision Lucas
// D-2026-07-04-10.1) : FORFAIT fixe ou TARIF HORAIRE. Les unités jour/semaine/
// mois sont supprimées (facturation fausse). Synchronisé avec
// backend/services/listing_guards.py → SERVICE_ALLOWED_UNITS = ("hour","fixed").
export const SERVICE_UNITS = [
  { id: "fixed", label: "forfait", short: "Forfait", suffix: "" },
  { id: "hour", label: "à l'heure", short: "À l'heure", suffix: "/h" },
];

/** L'annonce service est-elle facturée au forfait (prix fixe) ? */
export const isServiceFixed = (listing) =>
  isServiceListing(listing) && (listing.rental_unit || "hour") === "fixed";

/** Catégorie éligible à la location ? */
export const isRentalCategory = (category) =>
  RENTAL_ALLOWED_CATEGORIES.includes(category);

/** Catégorie éligible aux services (à l'heure) ? */
export const isServiceCategory = (category) =>
  SERVICE_ALLOWED_CATEGORIES.includes(category);

/** L'annonce est-elle une location (et non une vente) ? */
export const isRentalListing = (listing) =>
  !!listing && listing.listing_type === "rental";

/** L'annonce est-elle un service à l'heure ? */
export const isServiceListing = (listing) =>
  !!listing && listing.listing_type === "service";

/** L'annonce est-elle une location OU un service (≠ vente) ? */
export const isBookingListing = (listing) =>
  isRentalListing(listing) || isServiceListing(listing);

/** L'annonce est-elle GRATUITE (Phase 15) ? Mise en relation sans transaction
 *  (don d'objet, prêt, recherche de bénévoles…). Aucun paiement, aucun don,
 *  remise en main propre, contact direct via la messagerie.
 *  ⚠️ Synchronisé avec backend/services/listing_guards.py → is_free(). */
export const isFreeListing = (listing) =>
  !!listing && listing.listing_type === "free";

/** L'annonce n'a-t-elle PAS d'achat en ligne (location, service OU gratuit) ?
 *  Ces types affichent « Contacter » au lieu d'« Acheter » et forcent la
 *  remise en main propre. */
export const isContactOnlyListing = (listing) =>
  isBookingListing(listing) || isFreeListing(listing);

/** L'annonce est-elle réservée (offre acceptée non encore payée) ? On
 *  n'expose volontairement PAS la durée — juste l'état. */
export const isReserved = (listing) => {
  if (!listing?.reserved_until) return false;
  const t = Date.parse(listing.reserved_until);
  return !Number.isNaN(t) && t > Date.now();
};

/** Libellé/style du badge selon le type (location / service).
 *  PAS de badge « Gratuit » : pour une annonce gratuite, le prix affiché vaut
 *  déjà « Gratuit » (cf. formatListingPrice) — un badge « Gratuit » en plus
 *  ferait doublon sur les cartes et la fiche. Un free n'a donc pas de badge. */
export const bookingBadge = (listing) => {
  if (isServiceListing(listing)) return { label: "Service", emoji: "🤝" };
  if (isRentalListing(listing)) return { label: "À louer", emoji: "📅" };
  return null;
};

const _unit = (unitId) =>
  RENTAL_UNITS.find((u) => u.id === (unitId || DEFAULT_RENTAL_UNIT));

/** Suffixe prix court, ex. "/jour". "" si unité inconnue. */
export const rentalUnitSuffix = (unitId) => {
  const u = _unit(unitId);
  return u ? u.suffix : "";
};

/** Libellé long, ex. "par jour". */
export const rentalUnitLabel = (unitId) => {
  const u = _unit(unitId);
  return u ? u.label : "";
};

/**
 * Prix formaté pour une annonce. Annonce gratuite → "Gratuit" (jamais
 * "0,00 €"). Location → suffixe d'unité ("20,00 €/jour"). Vente → standard.
 */
export const formatListingPrice = (listing) => {
  if (isFreeListing(listing)) return "Gratuit";
  // Format FR : séparateur virgule (« 20,00 € »), jamais le point anglo-saxon.
  const price = Number(listing?.price || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (isBookingListing(listing)) {
    return `${price} €${rentalUnitSuffix(listing.rental_unit)}`;
  }
  return `${price} €`;
};
